import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@activecollab/components";
import classNames from "classnames";
import { Field, Form, Formik, useFormikContext } from "formik";
import PropTypes from "prop-types";
import { keyCodes } from "~/utils/key_codes.jsx";
import { DependencyFormValidator } from "./DependencyFormValidator.js";
import { DependencySelector } from "./DependencySelector.jsx";

const DependencyForm = ({
  handleCancel,
  taskId,
  tasks,
  projectId,
  isAddNewEnabled,
  taskListId,
  fetching,
  onSubmit,
  className,
}) => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (values) => {
      typeof handleCancel === "function" && handleCancel();
      typeof onSubmit === "function" &&
        onSubmit({
          dependency_id: values.dependencyId,
          created_from: values.created_from,
          dependency_type: values.isParent ? "parent" : "child",
          is_parent: values.isParent,
        });
    },
    [handleCancel, onSubmit]
  );

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === keyCodes.escape && typeof handleCancel === "function") {
        handleCancel();
      }
    },
    [handleCancel]
  );

  return (
    <Formik
      initialValues={{
        dependencyId: 0,
        created_from: "",
        isParent: true,
      }}
      validationSchema={DependencyFormValidator}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {({ setFieldValue }) => (
        <Form
          className={classNames("tw-p-2 tw-bg-theme-200 tw-rounded-lg", className)}
          onKeyDown={handleKeyPress}
        >
          <div className="subtask_form">
            <div className="slim_form_column_wrapper">
              <ul className="dependency_parent_child_radio_buttons">
                <li>
                  <label>
                    <Field name="isParent">
                      {({ field }) => (
                        <input
                          {...field}
                          type="radio"
                          checked={field.value === true}
                          onChange={() => setFieldValue("isParent", true)}
                        />
                      )}
                    </Field>
                    <span className="dependency_radio_label_text">{t("Predecessor")}</span>
                  </label>
                </li>
                <li>
                  <label>
                    <Field name="isParent">
                      {({ field }) => (
                        <input
                          {...field}
                          type="radio"
                          checked={field.value === false}
                          onChange={() => setFieldValue("isParent", false)}
                        />
                      )}
                    </Field>
                    <span className="dependency_radio_label_text">{t("Successor")}</span>
                  </label>
                </li>
              </ul>
              <div className="form_block tw-mt-2 tw-mb-1">
                <DependencySelectorWrapper
                  taskId={taskId}
                  projectId={projectId}
                  taskListId={taskListId}
                  options={tasks}
                  isAddNewEnabled={isAddNewEnabled}
                  fetching={fetching}
                />
              </div>
              <div className="dependency_info">
                {t("Predecessors should be completed before the current task.")}
                {t("Member assigned to the current task will be notified when each predecessor is finished.")}
              </div>
            </div>
          </div>
          <div className="tw-mt-2">
            <Button
              type="submit"
              variant="primary"
              className="tw-mr-2"
            >
              {t("Add")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("Cancel")}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const DependencySelectorWrapper = ({ taskId, projectId, taskListId, options, isAddNewEnabled, fetching }) => {
  const { setFieldValue, values, errors, setFieldTouched, touched } = useFormikContext();

  const handleSelectDependency = useCallback(
    (dependencyId, created_from) => {
      setFieldValue("dependencyId", dependencyId);
      setFieldValue("created_from", created_from);
      setFieldTouched("created_from", false);
    },
    [setFieldTouched, setFieldValue]
  );

  const handleClearDependency = useCallback(() => {
    setFieldValue("dependencyId", 0);
    setFieldValue("created_from", "");
  }, [setFieldValue]);

  return (
    <DependencySelector
      taskId={taskId}
      projectId={projectId}
      taskListId={taskListId}
      options={options}
      dependencyId={values.dependencyId}
      onSelect={handleSelectDependency}
      onClear={handleClearDependency}
      isAddNewEnabled={isAddNewEnabled}
      fetching={fetching}
      invalid={!!errors.created_from && !!touched.created_from}
      errorMessage={errors?.created_from}
    />
  );
};

DependencyForm.propTypes = {
  onSubmit: PropTypes.func,
  handleCancel: PropTypes.func,
  fetching: PropTypes.bool,
  tasks: PropTypes.array,
  projectId: PropTypes.number,
  taskId: PropTypes.number,
  taskListId: PropTypes.number,
  isAddNewEnabled: PropTypes.bool,
  className: PropTypes.string,
};

DependencyForm.defaultProps = {
  tasks: [],
  fetching: false,
  isAddNewEnabled: true,
};

DependencySelectorWrapper.propTypes = {
  taskId: PropTypes.number,
  projectId: PropTypes.number,
  taskListId: PropTypes.number,
  options: PropTypes.array,
  isAddNewEnabled: PropTypes.bool,
  fetching: PropTypes.bool,
};

DependencySelectorWrapper.defaultProps = {
  options: [],
  fetching: false,
  isAddNewEnabled: true,
};

export default DependencyForm;
